/**
 * Preferência de aparência da área logada (espelha `apps/mobile/src/lib/appearance-storage.ts`).
 * `system` segue o `prefers-color-scheme` do SO/navegador.
 * A chave e o formato precisam permanecer iguais ao script inline em `index.html`.
 */
export const APPEARANCE_STORAGE_KEY = "sysigreja.appearance";
const STATE_VERSION = 1;

export type AppearanceMode = "system" | "light" | "dark";
export type ColorSchemeName = "light" | "dark";

export type AppearanceState = {
  version: number;
  mode: AppearanceMode;
  /**
   * Flag `TemaEscuroWeb` vista na última sessão logada.
   * O boot do `index.html` só aplica escuro em `/sistema` quando isto (ou o
   * cache do Unleash) indica que o tema escuro está liberado.
   */
  darkAvailable?: boolean;
};

export const DEFAULT_APPEARANCE_STATE: AppearanceState = {
  version: STATE_VERSION,
  mode: "system",
};

export const LIGHT_THEME_COLOR = "#ffffff";
export const DARK_THEME_COLOR = "#0b0f14";

function sanitizeMode(value: unknown): AppearanceMode {
  if (value === "light" || value === "dark" || value === "system") {
    return value;
  }
  return "system";
}

export function getAppearanceState(): AppearanceState {
  try {
    const raw = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_APPEARANCE_STATE };

    const parsed = JSON.parse(raw) as Partial<AppearanceState>;
    return {
      version: typeof parsed.version === "number" ? parsed.version : STATE_VERSION,
      mode: sanitizeMode(parsed.mode),
      darkAvailable: parsed.darkAvailable === true,
    };
  } catch {
    return { ...DEFAULT_APPEARANCE_STATE };
  }
}

export function saveAppearanceState(state: AppearanceState): void {
  try {
    const payload: AppearanceState = {
      version: STATE_VERSION,
      mode: sanitizeMode(state.mode),
    };
    if (state.darkAvailable === true) {
      payload.darkAvailable = true;
    }
    localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage indisponível (modo privado, quota)
  }
}

export function getSystemColorScheme(): ColorSchemeName {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveColorScheme(
  mode: AppearanceMode,
  systemScheme: ColorSchemeName,
): ColorSchemeName {
  if (mode === "light" || mode === "dark") return mode;
  return systemScheme;
}

export function isSistemaPath(pathname = window.location.pathname): boolean {
  return pathname === "/sistema" || pathname.startsWith("/sistema/");
}

function readUnleashTemaEscuroWeb(): boolean {
  try {
    const raw = localStorage.getItem("unleash:repository:repo");
    if (!raw) return false;
    const parsed = JSON.parse(raw) as unknown;
    const list = Array.isArray(parsed)
      ? parsed
      : parsed && typeof parsed === "object"
        ? (parsed as { toggles?: unknown; features?: unknown }).toggles ||
          (parsed as { features?: unknown }).features ||
          Object.values(parsed)
        : [];
    if (!Array.isArray(list)) return false;
    return list.some(
      (flag) =>
        flag &&
        typeof flag === "object" &&
        (flag as { name?: string }).name === "TemaEscuroWeb" &&
        (flag as { enabled?: boolean }).enabled === true,
    );
  } catch {
    return false;
  }
}

/** Indica se o boot pode aplicar escuro (flag persistida ou cache do Unleash). */
export function isDarkThemeAvailable(): boolean {
  const state = getAppearanceState();
  if (state.darkAvailable === true || state.mode === "dark") return true;
  return readUnleashTemaEscuroWeb();
}

function updateThemeMetas(scheme: ColorSchemeName): void {
  const colorSchemeMeta = document.querySelector('meta[name="color-scheme"]');
  if (colorSchemeMeta) {
    colorSchemeMeta.setAttribute("content", scheme);
  }

  const themeColor = scheme === "dark" ? DARK_THEME_COLOR : LIGHT_THEME_COLOR;
  document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
    if (!meta.getAttribute("media")) {
      meta.setAttribute("content", themeColor);
    }
  });
}

/** Aplica o scheme no `<html>` (classe `dark`/`light`, `data-theme` e `color-scheme`). */
export function applyColorScheme(scheme: ColorSchemeName): void {
  const root = document.documentElement;
  root.classList.remove(scheme === "dark" ? "light" : "dark");
  root.classList.add(scheme);
  root.setAttribute("data-theme", scheme);
  root.style.colorScheme = scheme;
  updateThemeMetas(scheme);
}
